(() => {
  const getOptionInput = (label) => {
    if (!label) return null;
    if (label.control instanceof HTMLInputElement) return label.control;

    const inputId = label.getAttribute('for');
    if (inputId) {
      const linkedInput = document.getElementById(inputId);
      if (linkedInput instanceof HTMLInputElement) return linkedInput;
    }

    const node = label.closest('.m-product-option--node');
    const nestedInput = node?.querySelector('input');
    return nestedInput instanceof HTMLInputElement ? nestedInput : null;
  };

  const hasUnavailableMarker = (element) => {
    if (!element) return false;

    const classText = typeof element.className === 'string' ? element.className.toLowerCase() : '';
    const available = element.getAttribute?.('data-available');
    const soldOut = element.getAttribute?.('data-sold-out');
    const ariaDisabled = element.getAttribute?.('aria-disabled');

    return (
      /(^|[\s_-])(disabled|soldout|sold-out|unavailable|is-disabled)([\s_-]|$)/.test(classText) ||
      available === 'false' ||
      soldOut === 'true' ||
      ariaDisabled === 'true'
    );
  };

  const isUnavailableOption = (label) => {
    const input = getOptionInput(label);
    const node = label?.closest('.m-product-option--node');

    return Boolean(
      input?.disabled ||
      input?.matches(':disabled') ||
      hasUnavailableMarker(input) ||
      hasUnavailableMarker(label) ||
      hasUnavailableMarker(node)
    );
  };

  const markUnavailableOptions = (scope = document) => {
    const labels = scope.querySelectorAll?.('.m-product-option--node__label') || [];

    labels.forEach((label) => {
      if (isUnavailableOption(label)) {
        label.dataset.ifSoldoutOption = 'true';
        label.setAttribute('aria-disabled', 'true');
      } else {
        delete label.dataset.ifSoldoutOption;
        if (label.getAttribute('aria-disabled') === 'true') {
          label.removeAttribute('aria-disabled');
        }
      }
    });
  };

  const setPurchaseVisibility = (root, available) => {
    const block = root?.querySelector('[data-inayah-purchase-actions], .main-product__block-buy_buttons');
    if (!block) return;

    const isAvailable = available === true;
    block.dataset.variantAvailable = String(isAvailable);

    if (isAvailable) {
      block.hidden = false;
      block.style.removeProperty('display');
    } else {
      block.hidden = true;
      block.style.setProperty('display', 'none', 'important');
    }
  };

  const inferCurrentAvailability = (root) => {
    const block = root?.querySelector('[data-inayah-purchase-actions], .main-product__block-buy_buttons');
    if (!block) return false;

    if (block.dataset.variantAvailable === 'true') return true;
    if (block.dataset.variantAvailable === 'false') return false;

    const button = block.querySelector('.m-add-to-cart');
    if (!button) return false;

    return !(
      button.disabled ||
      button.classList.contains('disabled') ||
      /sold\s*out|unavailable/i.test(button.textContent || '')
    );
  };

  const getIndiaCalendarDate = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date());

    const values = parts.reduce((result, part) => {
      if (part.type !== 'literal') result[part.type] = part.value;
      return result;
    }, {});

    return new Date(Date.UTC(
      Number(values.year),
      Number(values.month) - 1,
      Number(values.day),
      12
    ));
  };

  const addCalendarDays = (date, days) => new Date(date.getTime() + days * 86400000);

  const formatTimelineDate = (date) => new Intl.DateTimeFormat('en-IN', {
    timeZone: 'UTC',
    month: 'short',
    day: '2-digit'
  }).format(date);

  const deliveryTimelineMarkup = () => {
    const today = getIndiaCalendarDate();
    const shipping = addCalendarDays(today, 1);
    const deliveryStart = addCalendarDays(today, 4);
    const deliveryEnd = addCalendarDays(today, 5);

    return `
      <div class="if-delivery-timeline" data-inayah-delivery-timeline aria-label="Estimated order and delivery dates">
        <div class="if-delivery-timeline__step">
          <span class="if-delivery-timeline__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 7h12l-1 13H7L6 7Z"></path>
              <path d="M9 9V6a3 3 0 0 1 6 0v3"></path>
              <path d="m9.5 14 1.6 1.6 3.6-3.8"></path>
            </svg>
          </span>
          <span class="if-delivery-timeline__label">Order Date</span>
          <span class="if-delivery-timeline__value">Today · ${formatTimelineDate(today)}</span>
        </div>
        <div class="if-delivery-timeline__step">
          <span class="if-delivery-timeline__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 7h11v9H3z"></path>
              <path d="M14 10h3l3 3v3h-6z"></path>
              <circle cx="7" cy="18" r="2"></circle>
              <circle cx="17" cy="18" r="2"></circle>
              <path d="M5 7V5h7v2"></path>
            </svg>
          </span>
          <span class="if-delivery-timeline__label">Estimated Shipping</span>
          <span class="if-delivery-timeline__value">${formatTimelineDate(shipping)}</span>
          <span class="if-delivery-timeline__caption">Next day</span>
        </div>
        <div class="if-delivery-timeline__step">
          <span class="if-delivery-timeline__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z"></path>
              <circle cx="12" cy="10" r="2.5"></circle>
            </svg>
          </span>
          <span class="if-delivery-timeline__label">Estimated Delivery</span>
          <span class="if-delivery-timeline__value">${formatTimelineDate(deliveryStart)} – ${formatTimelineDate(deliveryEnd)}</span>
          <span class="if-delivery-timeline__caption">4–5 days</span>
        </div>
      </div>`;
  };

  const ensureDeliveryTimeline = (wrapper, razorpayBlock) => {
    let timelineBlock = wrapper.querySelector('.if-delivery-timeline-block');

    if (!timelineBlock) {
      timelineBlock = document.createElement('div');
      timelineBlock.className = 'main-product__block if-delivery-timeline-block';
    }

    timelineBlock.innerHTML = deliveryTimelineMarkup();
    razorpayBlock.insertAdjacentElement('afterend', timelineBlock);
    return timelineBlock;
  };

  const arrangeProductPurchaseArea = (scope = document) => {
    const productRoots = scope.matches?.('.m-main-product')
      ? [scope]
      : scope.querySelectorAll?.('.m-main-product') || [];

    productRoots.forEach((root) => {
      const wrapper = root.querySelector('.m-product-info--wrapper');
      if (!wrapper) return;

      const variantBlock = wrapper.querySelector('.main-product__block-variant_picker');
      const buyButtonsBlock = wrapper.querySelector('[data-inayah-purchase-actions], .main-product__block-buy_buttons');

      if (variantBlock) {
        variantBlock.classList.add('if-product-variants');
        markUnavailableOptions(variantBlock);
      }

      if (buyButtonsBlock) {
        buyButtonsBlock.classList.add('if-product-purchase-actions');
        setPurchaseVisibility(root, inferCurrentAvailability(root));
      }

      const directBlocks = Array.from(wrapper.children).filter((element) =>
        element.classList.contains('main-product__block')
      );
      const priceBlock = wrapper.querySelector('.main-product__block-price');
      const razorpayElement = wrapper.querySelector(
        '[id*="razorpay_trusted_business"], [data-block-id*="razorpay"], [class*="razorpay"], iframe[src*="razorpay"]'
      );

      let razorpayBlock = razorpayElement?.closest('.main-product__block') || null;
      if (!razorpayBlock && priceBlock) {
        const priceIndex = directBlocks.indexOf(priceBlock);
        razorpayBlock = priceIndex >= 0 ? directBlocks[priceIndex + 1] : null;
      }

      if (!razorpayBlock || !variantBlock || !buyButtonsBlock) return;

      const timelineBlock = ensureDeliveryTimeline(wrapper, razorpayBlock);
      const descriptionBlock = wrapper.querySelector('.main-product__block-description');
      const metaBlock = wrapper.querySelector('.main-product__block-meta');
      const sizeChartButton = descriptionBlock?.querySelector('#open-size-chart') || wrapper.querySelector('#open-size-chart');
      let insertionPoint = timelineBlock;

      if (sizeChartButton) {
        sizeChartButton.classList.add('if-size-chart-relocated');
        sizeChartButton.removeAttribute('style');
        insertionPoint.insertAdjacentElement('afterend', sizeChartButton);
        insertionPoint = sizeChartButton;
      }

      insertionPoint.insertAdjacentElement('afterend', variantBlock);
      variantBlock.insertAdjacentElement('afterend', buyButtonsBlock);

      let detailsAnchor = buyButtonsBlock;
      if (metaBlock) {
        metaBlock.classList.add('if-product-sku');
        buyButtonsBlock.insertAdjacentElement('afterend', metaBlock);
        detailsAnchor = metaBlock;
      }

      if (descriptionBlock) {
        descriptionBlock.classList.add('if-product-details');
        detailsAnchor.insertAdjacentElement('afterend', descriptionBlock);
      }

      wrapper.dataset.ifProductOrderReady = 'true';
    });
  };

  const handleVariantChange = (event) => {
    const root = document.querySelector('.m-main-product');
    if (!root) return;

    const variant = event.detail?.variant;
    markUnavailableOptions(root);
    setPurchaseVisibility(root, Boolean(variant?.available));
  };

  const init = () => arrangeProductPurchaseArea(document);

  document.addEventListener('variant:changed', handleVariantChange);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    arrangeProductPurchaseArea(event.target);
  });
})();
